import log from "loglevel";
import { reportError } from "services/telemetry";
import OnReloadFullScreenLoader from "Components/OnReloadFullScreenLoader";
import React, { useEffect, useState } from "react";
import { Button, Alert, Container, Row, Col, Image } from "react-bootstrap";

// Stack traces are for developers, not end users. CRA sets NODE_ENV to "development" for `npm start` and "production" for `npm run build`, so the details toggle is only ever rendered locally.
const SHOW_ERROR_DETAILS = process.env.NODE_ENV === "development";

// A stale chunk after a deploy is recoverable with a single reload. The sessionStorage guard makes sure we reload at most once per failing chunk, so a genuinely missing chunk can't cause a reload loop.
function reloadForChunkError(error) {
  if (error?.name !== "ChunkLoadError") return false;
  try {
    if (sessionStorage.getItem("chunkReloadedFor") === error.message) return false;
    sessionStorage.setItem("chunkReloadedFor", error.message);
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

function ErrorFallback({ error, errorInfo }) {
  const [showDetails, setShowDetails] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    // getDerivedStateFromError renders us before componentDidCatch supplies errorInfo; wait for it so the error is logged and reported exactly once.
    if (!errorInfo) return;

    log.log("---- Error Boundary ----");
    log.error(error);
    log.error(errorInfo);
    log.log("---- -------------- ----");

    if (reloadForChunkError(error)) {
      setReloading(true);
      return;
    }


    reportError({
      message: error?.message,
      stack: error?.stack,
      excType: error?.name || "Error",
      context: {
        kind: "error_boundary",
        componentStack: errorInfo?.componentStack,
      },
    });


    if (error?.name === "ChunkLoadError" && sessionStorage.getItem("chunkReloadedFor") !== error.message) {
      sessionStorage.setItem("chunkReloadedFor", error.message);
      window.location.reload();
      return;
    }


  }, [error, errorInfo]);


  if (reloading) {
    return <OnReloadFullScreenLoader />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        minHeight: "100svh",
        background: "rgb(223 240 255)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem"
      }}
    >
      <div>
        <Image
          src='/images/default_image.png'
          alt='Bodhitree Logo'
        />
      </div>
      <Container>
        <Row className="justify-content-center">
          <Col md={10} lg={8} xl={6}>
            <div
              className="error-card p-4 rounded-lg shadow"
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.08)"
              }}
            >
              <div className="text-center mb-4">
                <div className="mb-4" style={{ fontSize: "60px" }}>
                  😕
                </div>
                <h2 className="fw-bold mb-2" style={{ color: "#3a3a3a" }}>Oops! Something went wrong</h2>
                <p className="text-muted mb-4" style={{ fontSize: "16px" }}>
                  We're sorry for the inconvenience. The application encountered an unexpected error.
                </p>
              </div>

              <OnReloadFullScreenLoader />

              {SHOW_ERROR_DETAILS && (
                <div className="d-flex justify-content-center mb-4">
                  <Button
                    variant="link"
                    onClick={() => setShowDetails(prev => !prev)}
                    className="text-decoration-none"
                    style={{ fontSize: "14px" }}
                  >
                    {showDetails ? "Hide" : "Show"} Technical Details
                  </Button>
                </div>
              )}

              {SHOW_ERROR_DETAILS && showDetails && error && (
                <Alert
                  variant="light"
                  className="text-start mb-4 overflow-auto"
                  style={{
                    maxHeight: "200px",
                    fontSize: "14px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    borderRadius: "8px"
                  }}
                >
                  <p className="fw-bold mb-1">Error:</p>
                  <p className="mb-2" style={{ color: "#dc3545" }}>{error.toString()}</p>

                  {errorInfo && (
                    <>
                      <p className="fw-bold mb-1">Component Stack:</p>
                      <pre className="mb-0" style={{ fontSize: "12px", color: "#6c757d" }}>
                        {errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </Alert>
              )}

              <div className="d-flex gap-3 justify-content-center">
                <Button
                  variant="primary"
                  onClick={() => window.location.reload()}
                  className="px-4 py-2"
                  style={{
                    borderRadius: "8px",
                    fontWeight: "500",
                    border: "none",
                  }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Reload Page
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

// React (as of 18.x) only exposes error catching through class lifecycles; there is no hook equivalent. This shell does nothing but capture the error and hand it to the function component above.
class ErrorBoundaryShell extends React.Component {
  state = { error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} />;
    }
    return this.props.children;
  }
}


export default function ErrorBoundary({ children }) {
  return <ErrorBoundaryShell>{children}</ErrorBoundaryShell>;
}

